import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { notificationService } from '../services/notificationService';
import { DailyReminder, NotificationPermissionState, FCMTokenData } from '../types/user';

interface UseReminderReturn {
  reminder: DailyReminder | null;
  permissionState: NotificationPermissionState;
  loading: boolean;
  error: string | null;
  requestPermission: () => Promise<boolean>;
  setReminder: (hour: number) => Promise<void>;
  deleteReminder: () => Promise<void>;
  refreshReminder: () => Promise<void>;
}

export const useReminder = (): UseReminderReturn => {
  const { currentUser } = useAuth();
  const [reminder, setReminderState] = useState<DailyReminder | null>(null);
  const [permissionState, setPermissionState] = useState<NotificationPermissionState>(
    notificationService.getPermissionState()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user's existing reminder
  const loadReminder = useCallback(async () => {
    if (!currentUser?.uid) return;

    setLoading(true);
    setError(null);

    try {
      const existingReminder = await notificationService.getUserReminder(currentUser.uid);
      setReminderState(existingReminder);

      // `gmtHour` is fixed at the zone offset in effect when the reminder was
      // saved and never re-derived, so it silently drifts to the wrong local
      // time after travel OR a DST change within the same named zone (e.g.
      // "America/New_York" doesn't change name across the DST boundary, but
      // its UTC offset does). Recompute what gmtHour *should* be right now
      // for the stored localHour/timezone and only resync if it actually
      // differs from what's stored — best-effort, doesn't block the loading
      // state, and is self-limiting since the next load sees the corrected
      // value. `saveDailyReminder` writes the new reminder before removing
      // the old one, so a failure here can't leave the user with none.
      if (existingReminder) {
        const expectedGmtHour = notificationService.convertToGMT(
          existingReminder.localHour,
          notificationService.getUserTimezone(),
        );
        if (expectedGmtHour !== existingReminder.gmtHour) {
          notificationService
            .saveDailyReminder(currentUser.uid, existingReminder.localHour)
            .catch((err) => console.warn('Failed to re-sync reminder timezone:', err));
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reminder');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Request notification permission
 const requestPermission = async (): Promise<boolean> => {
  setError(null);
  try {
    const granted = await notificationService.requestPermission();
    setPermissionState(notificationService.getPermissionState());
    
    if (granted && currentUser?.uid) {
      try {
        // ── ADD THIS ──
        await notificationService.waitForMessagingReady();
        // ─────────────
        await notificationService.addFCMTokenToUser(currentUser.uid, true);
      } catch (tokenError) {
        console.warn('Failed to add FCM token:', tokenError);
      }
    }
    return granted;
  } catch (err) {
    setError(err instanceof Error ? err.message : 'Failed to request permission');
    return false;
  }
};

  // Set a new reminder
  const setReminder = async (hour: number): Promise<void> => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    if (hour < 0 || hour > 23) {
      throw new Error('Hour must be between 0 and 23');
    }

    setLoading(true);
    setError(null);

    try {
      // Check permissions first
      if (!permissionState.granted) {
        const granted = await requestPermission();
        if (!granted) {
          throw new Error('Notification permission is required to set reminders');
        }
      }

      // Set new reminder — saveDailyReminder migrates any existing reminder
      // itself (writes the new bucket, then removes the old one), so no
      // separate pre-delete here (that ordering used to risk losing the
      // reminder entirely if a step after the delete failed).
      await notificationService.saveDailyReminder(currentUser.uid, hour);

      // Reload reminder data
      await loadReminder();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set reminder');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete reminder
  const deleteReminder = async (): Promise<void> => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    setLoading(true);
    setError(null);

    try {
      await notificationService.deleteReminder(currentUser.uid);
      setReminderState(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete reminder');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Refresh reminder data
  const refreshReminder = async (): Promise<void> => {
    await loadReminder();
  };

  // Load reminder on mount and when user changes
  useEffect(() => {
    if (currentUser?.uid) {
      loadReminder();
    } else {
      setReminderState(null);
    }
  }, [currentUser?.uid, loadReminder]);

  // Update permission state when it changes
  useEffect(() => {
    const handlePermissionChange = () => {
      setPermissionState(notificationService.getPermissionState());
    };

    // Listen for permission changes (though this doesn't work in all browsers)
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' }).then((result) => {
        result.addEventListener('change', handlePermissionChange);
        return () => result.removeEventListener('change', handlePermissionChange);
      });
    }

    // Fallback: check permission state periodically
    const interval = setInterval(handlePermissionChange, 5000);
    return () => clearInterval(interval);
  }, []);

  return {
    reminder,
    permissionState,
    loading,
    error,
    requestPermission,
    setReminder,
    deleteReminder,
    refreshReminder
  };
};

// Hook for managing FCM tokens across multiple devices
interface UseFCMTokenManagementReturn {
  tokens: FCMTokenData[];
  loading: boolean;
  error: string | null;
  refreshTokens: () => Promise<void>;
  toggleDeviceNotifications: (deviceId: string, enabled: boolean) => Promise<void>;
  addCurrentDevice: () => Promise<void>;
  removeInvalidToken: (token: string) => Promise<void>;
}

export const useFCMTokenManagement = (): UseFCMTokenManagementReturn => {
  const { currentUser } = useAuth();
  const [tokens, setTokens] = useState<FCMTokenData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user's FCM tokens
  const loadTokens = useCallback(async () => {
    if (!currentUser?.uid) return;

    setLoading(true);
    setError(null);

    try {
      const userTokens = await notificationService.getUserFCMTokens(currentUser.uid);
      setTokens(userTokens);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tokens');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Refresh tokens data
  const refreshTokens = async (): Promise<void> => {
    await loadTokens();
  };

  // Toggle notifications for a specific device
  const toggleDeviceNotifications = async (deviceId: string, enabled: boolean): Promise<void> => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    setError(null);

    try {
      await notificationService.toggleDeviceNotifications(currentUser.uid, deviceId, enabled);
      await loadTokens(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle device notifications');
      throw err;
    }
  };

  // Add current device to token list
  const addCurrentDevice = async (): Promise<void> => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    setError(null);

    try {
      await notificationService.addFCMTokenToUser(currentUser.uid, true);
      await loadTokens(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add current device');
      throw err;
    }
  };

  // Remove invalid token
  const removeInvalidToken = async (token: string): Promise<void> => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    setError(null);

    try {
      await notificationService.removeInvalidFCMToken(currentUser.uid, token);
      await loadTokens(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove token');
      throw err;
    }
  };

  // Load tokens on mount and when user changes
  useEffect(() => {
    if (currentUser?.uid) {
      loadTokens();
    } else {
      setTokens([]);
    }
  }, [currentUser?.uid, loadTokens]);

  return {
    tokens,
    loading,
    error,
    refreshTokens,
    toggleDeviceNotifications,
    addCurrentDevice,
    removeInvalidToken
  };
}; 